import React, { useState, useEffect, useCallback, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import FacturaRevisionModal from './FacturaRevisionModal';
import {
  getEmailIngesta,
  getEmailIngestaCounters,
  getGmailSyncConfig,
  updateGmailSyncConfig,
  marcarIgnorada,
  marcarNoFactura,
  eliminarFactura,
  renewGmailWatch,
  getAttachmentSignedUrl,
  reprocesarIngesta,
} from '../../../services/gmailIngestService';

// ── CONSTANTS ─────────────────────────────────────────────────────────────────
const ACCENT = '#34D399';
const GOLD = '#D6EF4E';
const BLUE = '#60A5FA';
const RED = '#F87171';
const AMBER = '#FBBF24';
const ORANGE = '#FB923C';
const PURPLE = '#A78BFA';
const CYAN = '#22D3EE';

const fmt = (n, d = 2) =>
  typeof n === 'number' ? n?.toLocaleString('es-ES', { minimumFractionDigits: d, maximumFractionDigits: d }) : '—';
const fmtEur = (n) => (typeof n === 'number' ? `${fmt(n)} €` : '—');
const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d)?.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return d; }
};

const ESTADO_CONFIG = {
  NUEVA:               { label: 'Nueva',              color: BLUE,   icon: 'Mail' },
  PROCESANDO:          { label: 'Procesando',          color: AMBER,  icon: 'Loader' },
  LISTA_PARA_REVISAR:  { label: 'Lista para revisar',  color: ACCENT, icon: 'CheckCircle' },
  REQUIERE_REVISION:   { label: 'Requiere revisión',   color: ORANGE, icon: 'AlertTriangle' },
  CONTABILIZADA:       { label: 'Contabilizada',       color: GOLD,   icon: 'PackageCheck' },
  DUPLICADA:           { label: 'Duplicada',           color: PURPLE, icon: 'Copy' },
  IGNORADA:            { label: 'Ignorada',            color: 'rgba(255,255,255,0.25)', icon: 'EyeOff' },
  ERROR:               { label: 'Error',               color: RED,    icon: 'XCircle' },
};

const ESTADO_OPTS = ['TODAS', ...Object.keys(ESTADO_CONFIG)];

// Motivo labels
const MOTIVO_LABELS = {
  proveedor_no_identificado: 'Proveedor no identificado',
  total_inconsistente: 'Total inconsistente',
  lineas_incompletas: 'Líneas incompletas',
  confianza_baja: 'Confianza OCR baja',
  fecha_faltante: 'Fecha faltante',
  ocr_parcial: 'OCR parcial',
  NO_ES_FACTURA: 'No es factura',
};

// ── ESTADO BADGE ──────────────────────────────────────────────────────────────
const EstadoBadge = ({ estado }) => {
  const cfg = ESTADO_CONFIG?.[estado] || { label: estado, color: 'rgba(255,255,255,0.4)', icon: 'Circle' };
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-oranienbaum whitespace-nowrap"
      style={{ background: `${cfg?.color}18`, color: cfg?.color, border: `1px solid ${cfg?.color}30` }}>
      <Icon name={cfg?.icon} size={9} />
      {cfg?.label}
    </span>
  );
};

// ── CONFIRM DIALOG ────────────────────────────────────────────────────────────
const ConfirmDialog = ({ title, message, details, confirmLabel, confirmColor, onConfirm, onCancel }) => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.90)' }}>
    <div className="w-full max-w-sm rounded-2xl" style={{ background: '#1a1a1a', border: '1px solid rgba(255,255,255,0.12)' }}>
      <div className="px-6 py-5">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${confirmColor || RED}15`, border: `1px solid ${confirmColor || RED}30` }}>
            <Icon name="AlertTriangle" size={16} style={{ color: confirmColor || RED }} />
          </div>
          <h3 className="font-oranienbaum font-bold text-white text-base">{title}</h3>
        </div>
        <p className="font-oranienbaum text-white/60 text-sm mb-3">{message}</p>
        {details && (
          <div className="p-3 rounded-xl mb-3" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
            {details?.map((d, i) => <p key={i} className="font-oranienbaum text-white/70 text-xs">{d}</p>)}
          </div>
        )}
      </div>
      <div className="flex gap-2 px-6 pb-5">
        <button onClick={onCancel}
          className="flex-1 px-4 py-2 rounded-xl text-sm font-oranienbaum"
          style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.60)', border: '1px solid rgba(255,255,255,0.10)' }}>
          Cancelar
        </button>
        <button onClick={onConfirm}
          className="flex-1 px-4 py-2 rounded-xl text-sm font-oranienbaum font-semibold"
          style={{ background: `${confirmColor || RED}20`, color: confirmColor || RED, border: `1px solid ${confirmColor || RED}40` }}>
          {confirmLabel || 'Confirmar'}
        </button>
      </div>
    </div>
  </div>
);

// ── ROW ACTIONS MENU ──────────────────────────────────────────────────────────
const RowActionsMenu = ({ row, onVerDetalle, onRevisar, onVerDocumento, onDescargar, onReprocesar, onNoFactura, onEliminar }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref?.current && !ref?.current?.contains(e?.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const canReprocess = row?.estado === 'ERROR' || row?.estado === 'REQUIERE_REVISION';
  const canRevisar = row?.estado === 'REQUIERE_REVISION' || row?.estado === 'ERROR';
  const canIgnore = row?.estado !== 'IGNORADA' && row?.estado !== 'CONTABILIZADA';

  const items = [
    { label: 'Ver detalle', icon: 'Eye', action: onVerDetalle, color: CYAN },
    canRevisar && { label: 'Revisar factura', icon: 'ClipboardEdit', action: onRevisar, color: ORANGE },
    row?.storage_path && { label: 'Ver documento', icon: 'FileText', action: onVerDocumento, color: BLUE },
    row?.storage_path && { label: 'Descargar documento', icon: 'Download', action: onDescargar, color: BLUE },
    canReprocess && row?.storage_path && { label: 'Reprocesar OCR', icon: 'RefreshCw', action: onReprocesar, color: AMBER },
    canIgnore && { label: 'Marcar como no factura', icon: 'Ban', action: onNoFactura, color: PURPLE },
    { label: 'Eliminar factura', icon: 'Trash2', action: onEliminar, color: RED, destructive: true },
  ]?.filter(Boolean);

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(o => !o)}
        className="p-1.5 rounded-lg transition-colors hover:bg-white/10 text-white/50 hover:text-white/80"
        title="Más acciones">
        <Icon name="MoreVertical" size={14} />
      </button>
      {open && (
        <div className="absolute right-0 top-8 z-40 w-52 rounded-xl py-1 shadow-2xl"
          style={{ background: '#1e1e1e', border: '1px solid rgba(255,255,255,0.12)' }}>
          {items?.map((item, i) => (
            <React.Fragment key={i}>
              {item?.destructive && i > 0 && <div style={{ height: '1px', background: 'rgba(255,255,255,0.07)', margin: '4px 0' }} />}
              <button
                onClick={() => { setOpen(false); item?.action?.(); }}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-xs font-oranienbaum transition-colors hover:bg-white/[0.06]"
                style={{ color: item?.color || 'rgba(255,255,255,0.70)' }}>
                <Icon name={item?.icon} size={13} />
                {item?.label}
              </button>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

// ── DETAIL MODAL ──────────────────────────────────────────────────────────────
const DetalleModal = ({ ingesta, onClose, onIgnorar, onReprocesar }) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loadingUrl, setLoadingUrl] = useState(false);
  const [reprocesando, setReprocesando] = useState(false);
  const [reprocesarError, setReprocesarError] = useState(null);

  useEffect(() => {
    if (ingesta?.storage_path) {
      setLoadingUrl(true);
      getAttachmentSignedUrl(ingesta?.storage_path, ingesta?.storage_bucket || 'facturas-compras')
        ?.then(url => setPdfUrl(url))?.catch(() => setPdfUrl(null))?.finally(() => setLoadingUrl(false));
    }
  }, [ingesta?.storage_path]);

  if (!ingesta) return null;

  const ocrData = ingesta?.ocr_metadata;
  const facturaOcr = ocrData?.factura || {};
  const proveedorOcr = ocrData?.proveedor || {};

  const handleReprocesar = async () => {
    setReprocesando(true);
    setReprocesarError(null);
    try {
      await onReprocesar(ingesta?.id);
      onClose();
    } catch (e) {
      setReprocesarError(e?.message || 'Error al reprocesar');
    } finally {
      setReprocesando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto" style={{ background: 'rgba(0,0,0,0.85)' }}>
      <div className="w-full max-w-2xl my-6 rounded-2xl flex flex-col" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.10)' }}>
        <div className="flex items-center justify-between px-6 py-4 flex-shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${BLUE}15`, border: `1px solid ${BLUE}30` }}>
              <Icon name="Mail" size={15} style={{ color: BLUE }} />
            </div>
            <div>
              <h3 className="font-oranienbaum font-bold text-white text-base">Detalle Email</h3>
              <p className="font-oranienbaum text-white/40 text-xs truncate max-w-xs">{ingesta?.subject || '(sin asunto)'}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-white/40 hover:text-white/80"><Icon name="X" size={16} /></button>
        </div>

        <div className="px-6 py-4 space-y-4 overflow-y-auto">
          <div className="flex items-center gap-3 flex-wrap">
            <EstadoBadge estado={ingesta?.estado} />
            {ingesta?.ocr_confianza != null && (
              <span className="font-oranienbaum text-white/40 text-xs">Confianza OCR: {Math.round(ingesta?.ocr_confianza)}%</span>
            )}
            {ingesta?.motivo_revision && (
              <span className="font-oranienbaum text-amber-400 text-xs px-2 py-0.5 rounded-full" style={{ background: `${AMBER}10`, border: `1px solid ${AMBER}25` }}>
                {MOTIVO_LABELS?.[ingesta?.motivo_revision] || ingesta?.motivo_revision}
              </span>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <p className="font-oranienbaum text-white/35 text-[10px] mb-1">Remitente</p>
              <p className="font-oranienbaum text-white/80 text-xs">{ingesta?.from_name || '—'}</p>
              <p className="font-oranienbaum text-white/45 text-[10px]">{ingesta?.from_email || '—'}</p>
            </div>
            <div className="p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <p className="font-oranienbaum text-white/35 text-[10px] mb-1">Recibido</p>
              <p className="font-oranienbaum text-white/80 text-xs">{fmtDate(ingesta?.received_at)}</p>
            </div>
            <div className="p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <p className="font-oranienbaum text-white/35 text-[10px] mb-1">Archivo</p>
              <p className="font-oranienbaum text-white/80 text-xs">{ingesta?.filename || '—'}</p>
              <p className="font-oranienbaum text-white/35 text-[10px]">{ingesta?.mime_type}</p>
            </div>
            <div className="p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <p className="font-oranienbaum text-white/35 text-[10px] mb-1">Proveedor detectado</p>
              <p className="font-oranienbaum text-white/80 text-xs">{ingesta?.proveedor?.nombre || ingesta?.proveedor_detectado || '—'}</p>
              {!ingesta?.proveedor_id && <p className="font-oranienbaum text-amber-400 text-[10px]">⚠ No reconocido</p>}
            </div>
          </div>

          {ocrData && (
            <div className="p-3 rounded-xl space-y-2" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <p className="font-oranienbaum text-white/35 text-[10px] uppercase tracking-wider">Datos OCR</p>
              <div className="grid grid-cols-3 gap-2 text-xs font-oranienbaum">
                <div><span className="text-white/35">Nº Factura: </span><span className="text-white/75">{facturaOcr?.numero_factura || '—'}</span></div>
                <div><span className="text-white/35">Fecha: </span><span className="text-white/75">{facturaOcr?.fecha || '—'}</span></div>
                <div><span className="text-white/35">Total: </span><span className="text-white/75">{facturaOcr?.total != null ? fmtEur(facturaOcr?.total) : '—'}</span></div>
                <div><span className="text-white/35">CIF: </span><span className="text-white/75">{proveedorOcr?.nif_cif || '—'}</span></div>
                <div><span className="text-white/35">Proveedor OCR: </span><span className="text-white/75">{proveedorOcr?.nombre || '—'}</span></div>
                <div><span className="text-white/35">Base: </span><span className="text-white/75">{facturaOcr?.base_imponible != null ? fmtEur(facturaOcr?.base_imponible) : '—'}</span></div>
              </div>
            </div>
          )}

          {ingesta?.error_mensaje && (
            <div className="p-3 rounded-xl" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.20)' }}>
              <p className="font-oranienbaum text-red-400 text-xs">
                <span className="font-semibold">Error ({ingesta?.error_stage}):</span> {ingesta?.error_mensaje}
              </p>
            </div>
          )}

          {reprocesarError && (
            <div className="p-3 rounded-xl" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.20)' }}>
              <p className="font-oranienbaum text-red-400 text-xs">{reprocesarError}</p>
            </div>
          )}

          {ingesta?.factura && (
            <div className="p-3 rounded-xl" style={{ background: `${ACCENT}08`, border: `1px solid ${ACCENT}20` }}>
              <p className="font-oranienbaum text-white/35 text-[10px] mb-1">Factura ERP vinculada</p>
              <p className="font-oranienbaum text-white/80 text-xs">
                {ingesta?.factura?.numero_factura} · {fmtEur(ingesta?.factura?.total)} · {ingesta?.factura?.estado}
              </p>
            </div>
          )}

          {ingesta?.storage_path && (
            <div className="p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <p className="font-oranienbaum text-white/35 text-[10px] mb-1">Documento en Storage</p>
              <p className="font-oranienbaum text-white/50 text-[10px] break-all">{ingesta?.storage_path}</p>
            </div>
          )}
        </div>

        <div className="flex gap-2 px-6 py-4 flex-shrink-0 flex-wrap" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          {pdfUrl && (
            <a href={pdfUrl} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-oranienbaum"
              style={{ background: `${BLUE}15`, color: BLUE, border: `1px solid ${BLUE}30` }}>
              <Icon name="FileText" size={12} />Ver PDF
            </a>
          )}
          {loadingUrl && (
            <span className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-oranienbaum text-white/40">
              <div className="w-3 h-3 rounded-full border border-white/20 border-t-white/60 animate-spin" />Cargando PDF...
            </span>
          )}
          {(ingesta?.estado === 'ERROR' || ingesta?.estado === 'REQUIERE_REVISION') && ingesta?.storage_path && (
            <button onClick={handleReprocesar} disabled={reprocesando}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-oranienbaum disabled:opacity-50"
              style={{ background: `${ORANGE}15`, color: ORANGE, border: `1px solid ${ORANGE}30` }}>
              {reprocesando
                ? <><div className="w-3 h-3 rounded-full border border-white/20 border-t-orange-400 animate-spin" />Reprocesando...</>
                : <><Icon name="RefreshCw" size={12} />Reprocesar</>}
            </button>
          )}
          {ingesta?.estado !== 'IGNORADA' && ingesta?.estado !== 'CONTABILIZADA' && (
            <button onClick={() => onIgnorar(ingesta?.id)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-oranienbaum"
              style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.50)', border: '1px solid rgba(255,255,255,0.10)' }}>
              <Icon name="EyeOff" size={12} />Marcar como no factura
            </button>
          )}
          <button onClick={onClose}
            className="ml-auto flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-oranienbaum"
            style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.60)', border: '1px solid rgba(255,255,255,0.10)' }}>
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

// ── CONFIG PANEL ──────────────────────────────────────────────────────────────
const ConfigPanel = ({ syncConfig, onClose, onSaved }) => {
  const [importFromDate, setImportFromDate] = useState(syncConfig?.import_from_date || '');
  const [saving, setSaving] = useState(false);
  const [renewLoading, setRenewLoading] = useState(false);
  const [renewResult, setRenewResult] = useState(null);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await updateGmailSyncConfig({ import_from_date: importFromDate || null });
      onSaved?.();
      onClose();
    } catch (e) {
      setError(e?.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRenewWatch = async () => {
    setRenewLoading(true);
    setRenewResult(null);
    setError(null);
    try {
      const result = await renewGmailWatch();
      setRenewResult(result);
    } catch (e) {
      setError(e?.message);
    } finally {
      setRenewLoading(false);
    }
  };

  const inputCls = 'w-full px-3 py-2 rounded-xl text-sm font-oranienbaum text-white/85 outline-none';
  const inputSt = { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }}>
      <div className="w-full max-w-md rounded-2xl" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.10)' }}>
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${CYAN}15`, border: `1px solid ${CYAN}30` }}>
              <Icon name="Settings" size={15} style={{ color: CYAN }} />
            </div>
            <h3 className="font-oranienbaum font-bold text-white text-base">Configuración Gmail</h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-white/40 hover:text-white/80"><Icon name="X" size={16} /></button>
        </div>

        <div className="px-6 py-4 space-y-4">
          <div className="p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
            <p className="font-oranienbaum text-white/35 text-[10px] uppercase tracking-wider mb-2">Estado Gmail Watch</p>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2 h-2 rounded-full" style={{ background: syncConfig?.watch_active ? ACCENT : RED }} />
              <span className="font-oranienbaum text-white/70 text-xs">{syncConfig?.watch_active ? 'Activo' : 'Inactivo'}</span>
            </div>
            {syncConfig?.watch_expiration && (
              <p className="font-oranienbaum text-white/35 text-[10px]">Expira: {fmtDate(syncConfig?.watch_expiration)}</p>
            )}
            {syncConfig?.history_id && (
              <p className="font-oranienbaum text-white/25 text-[10px]">HistoryId: {syncConfig?.history_id}</p>
            )}
          </div>

          <div>
            <label className="block font-oranienbaum text-white/45 text-xs mb-1">Importar emails desde</label>
            <input type="date" value={importFromDate} onChange={e => setImportFromDate(e?.target?.value)}
              className={inputCls} style={{ ...inputSt, colorScheme: 'dark' }} />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="p-2 rounded-lg text-center" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <p className="font-oranienbaum text-white/35 text-[10px]">Emails procesados</p>
              <p className="font-oranienbaum text-white font-bold text-sm">{syncConfig?.emails_procesados ?? 0}</p>
            </div>
            <div className="p-2 rounded-lg text-center" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <p className="font-oranienbaum text-white/35 text-[10px]">Facturas detectadas</p>
              <p className="font-oranienbaum text-white font-bold text-sm">{syncConfig?.facturas_detectadas ?? 0}</p>
            </div>
          </div>

          {renewResult && (
            <div className="p-3 rounded-xl" style={{ background: `${ACCENT}08`, border: `1px solid ${ACCENT}20` }}>
              <p className="font-oranienbaum text-xs" style={{ color: ACCENT }}>✓ Watch renovado. Expira: {fmtDate(renewResult?.expiration)}</p>
            </div>
          )}
          {error && (
            <div className="p-3 rounded-xl" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.20)' }}>
              <p className="font-oranienbaum text-red-400 text-xs">{error}</p>
            </div>
          )}
        </div>

        <div className="flex gap-2 px-6 py-4 flex-wrap" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <button onClick={handleRenewWatch} disabled={renewLoading}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-oranienbaum disabled:opacity-50"
            style={{ background: `${CYAN}15`, color: CYAN, border: `1px solid ${CYAN}30` }}>
            {renewLoading ? <><div className="w-3 h-3 rounded-full border border-white/20 border-t-white/60 animate-spin" />Renovando...</> : <><Icon name="RefreshCw" size={12} />Renovar Watch</>}
          </button>
          <button onClick={onClose}
            className="px-3 py-2 rounded-xl text-xs font-oranienbaum"
            style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.60)', border: '1px solid rgba(255,255,255,0.10)' }}>
            Cancelar
          </button>
          <button onClick={handleSave} disabled={saving}
            className="ml-auto flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-oranienbaum font-semibold disabled:opacity-50"
            style={{ background: `${ACCENT}20`, color: ACCENT, border: `1px solid ${ACCENT}40` }}>
            {saving ? 'Guardando...' : <><Icon name="Save" size={12} />Guardar</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
const BandejaEmail = () => {
  const [ingesta, setIngesta] = useState([]);
  const [counters, setCounters] = useState({});
  const [syncConfig, setSyncConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [estadoFilter, setEstadoFilter] = useState('TODAS');
  const [detalle, setDetalle] = useState(null);
  const [revision, setRevision] = useState(null); // row for FacturaRevisionModal
  const [showConfig, setShowConfig] = useState(false);
  const [reprocesandoId, setReprocesandoId] = useState(null);
  const [confirm, setConfirm] = useState(null); // { type, row }

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [rows, cnts, cfg] = await Promise.all([
        getEmailIngesta({ estado: estadoFilter }),
        getEmailIngestaCounters(),
        getGmailSyncConfig(),
      ]);
      setIngesta(rows);
      setCounters(cnts);
      setSyncConfig(cfg);
    } catch (e) {
      setError(e?.message || 'Error al cargar bandeja email');
    } finally {
      setLoading(false);
    }
  }, [estadoFilter]);

  useEffect(() => { load(); }, [load]);

  const handleIgnorar = async (id) => {
    try {
      await marcarIgnorada(id);
      await load();
      setDetalle(null);
    } catch (e) {
      setError(e?.message);
    }
  };

  const handleReprocesar = async (id) => {
    setReprocesandoId(id);
    setError(null);
    try {
      await reprocesarIngesta(id);
      await load();
      setDetalle(null);
    } catch (e) {
      setError(e?.message || 'Error al reprocesar');
      throw e;
    } finally {
      setReprocesandoId(null);
    }
  };

  const handleEliminar = async (row) => {
    try {
      await eliminarFactura(row?.id, row?.factura_id || null);
      await load();
    } catch (e) {
      setError(e?.message);
    }
  };

  const handleNoFactura = async (row) => {
    try {
      await marcarNoFactura(row?.id, row?.factura_id || null);
      await load();
    } catch (e) {
      setError(e?.message);
    }
  };

  const handleVerDocumento = async (row) => {
    if (!row?.storage_path) return;
    try {
      const url = await getAttachmentSignedUrl(row?.storage_path, row?.storage_bucket || 'facturas-compras');
      if (url) window.open(url, '_blank');
    } catch (e) {
      setError(e?.message);
    }
  };

  const handleDescargar = async (row) => {
    if (!row?.storage_path) return;
    try {
      const url = await getAttachmentSignedUrl(row?.storage_path, row?.storage_bucket || 'facturas-compras');
      if (url) {
        const a = document.createElement('a');
        a.href = url;
        a.download = row?.filename || 'factura.pdf';
        a?.click();
      }
    } catch (e) {
      setError(e?.message);
    }
  };

  const counterCards = [
    { key: 'LISTA_PARA_REVISAR', label: 'Listas para revisar', color: ACCENT, icon: 'CheckCircle' },
    { key: 'REQUIERE_REVISION',  label: 'Requieren revisión',  color: ORANGE, icon: 'AlertTriangle' },
    { key: 'NUEVA',              label: 'Nuevas',              color: BLUE,   icon: 'Mail' },
    { key: 'ERROR',              label: 'Errores',             color: RED,    icon: 'XCircle' },
    { key: 'DUPLICADA',          label: 'Duplicadas',          color: PURPLE, icon: 'Copy' },
    { key: 'total',              label: 'Total recibidas',     color: 'rgba(255,255,255,0.40)', icon: 'Inbox' },
  ];

  // Column headers — add MOTIVO for REQUIERE_REVISION filter
  const showMotivoCol = estadoFilter === 'REQUIERE_REVISION';

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: `${CYAN}15`, border: `1px solid ${CYAN}30` }}>
            <Icon name="Inbox" size={16} style={{ color: CYAN }} />
          </div>
          <div>
            <h2 className="font-oranienbaum font-bold text-white text-lg">Bandeja de Facturas Email</h2>
            <p className="font-oranienbaum text-white/35 text-xs">
              Facturas detectadas automáticamente en Gmail
              {syncConfig?.watch_active
                ? <span className="ml-2 text-green-400">● Watch activo</span>
                : <span className="ml-2 text-red-400">● Watch inactivo</span>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-oranienbaum"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.60)' }}>
            <Icon name="RefreshCw" size={12} />Actualizar
          </button>
          <button onClick={() => setShowConfig(true)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-oranienbaum"
            style={{ background: `${CYAN}15`, color: CYAN, border: `1px solid ${CYAN}30` }}>
            <Icon name="Settings" size={12} />Configurar Gmail
          </button>
        </div>
      </div>

      {/* Counter cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {counterCards?.map(c => (
          <button key={c?.key}
            onClick={() => setEstadoFilter(c?.key === 'total' ? 'TODAS' : c?.key)}
            className="rounded-xl p-3 flex flex-col gap-1.5 text-left transition-all"
            style={{
              background: estadoFilter === (c?.key === 'total' ? 'TODAS' : c?.key) ? `${c?.color}15` : 'rgba(255,255,255,0.03)',
              border: estadoFilter === (c?.key === 'total' ? 'TODAS' : c?.key) ? `1px solid ${c?.color}40` : '1px solid rgba(255,255,255,0.07)',
            }}>
            <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: `${c?.color}15` }}>
              <Icon name={c?.icon} size={12} style={{ color: c?.color }} />
            </div>
            <p className="font-oranienbaum font-bold text-white text-lg">{loading ? '...' : (counters?.[c?.key] ?? 0)}</p>
            <p className="font-oranienbaum text-white/40 text-[10px] leading-tight">{c?.label}</p>
          </button>
        ))}
      </div>

      {/* Filter tabs */}
      <div className="flex items-center gap-1 p-1 rounded-xl flex-wrap" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
        {ESTADO_OPTS?.map(s => (
          <button key={s} onClick={() => setEstadoFilter(s)}
            className="px-3 py-1.5 rounded-lg text-xs font-oranienbaum transition-all"
            style={estadoFilter === s
              ? { background: `${CYAN}20`, color: CYAN, border: `1px solid ${CYAN}40` }
              : { color: 'rgba(255,255,255,0.40)', border: '1px solid transparent' }}>
            {s === 'TODAS' ? 'Todas' : (ESTADO_CONFIG?.[s]?.label || s)}
            {s !== 'TODAS' && counters?.[s] > 0 && (
              <span className="ml-1 px-1 rounded text-[9px]" style={{ background: `${ESTADO_CONFIG?.[s]?.color}20`, color: ESTADO_CONFIG?.[s]?.color }}>
                {counters?.[s]}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* REQUIERE_REVISION queue notice */}
      {estadoFilter === 'REQUIERE_REVISION' && !loading && ingesta?.length > 0 && (
        <div className="p-4 rounded-xl flex items-start gap-3" style={{ background: `${ORANGE}08`, border: `1px solid ${ORANGE}20` }}>
          <Icon name="AlertTriangle" size={16} style={{ color: ORANGE }} className="flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-oranienbaum text-orange-300 text-sm font-semibold mb-0.5">Cola de revisión manual</p>
            <p className="font-oranienbaum text-white/50 text-xs">
              {ingesta?.length} factura{ingesta?.length !== 1 ? 's' : ''} requiere{ingesta?.length !== 1 ? 'n' : ''} revisión. Usa el botón "Revisar" en cada fila para corregir datos, asignar proveedor y aprobar.
            </p>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="p-4 rounded-xl flex items-center gap-3" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.20)' }}>
          <Icon name="AlertCircle" size={16} className="text-red-400 flex-shrink-0" />
          <p className="font-oranienbaum text-red-400 text-sm">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto p-1 text-red-400/60 hover:text-red-400"><Icon name="X" size={14} /></button>
        </div>
      )}

      {/* Setup notice */}
      {!syncConfig?.watch_active && !loading && (
        <div className="p-4 rounded-xl" style={{ background: `${AMBER}08`, border: `1px solid ${AMBER}20` }}>
          <div className="flex items-start gap-3">
            <Icon name="AlertTriangle" size={16} style={{ color: AMBER }} className="flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-oranienbaum text-amber-300 text-sm font-semibold mb-1">Gmail Watch no configurado</p>
              <p className="font-oranienbaum text-white/50 text-xs">
                Para recibir facturas automáticamente necesitas configurar las credenciales de Google OAuth y activar el Watch de Gmail.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.07)' }}>
        <div className="px-4 py-3 flex items-center justify-between" style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <span className="font-oranienbaum text-white/50 text-xs">{ingesta?.length} registros</span>
          {syncConfig?.ultima_sincronizacion && (
            <span className="font-oranienbaum text-white/25 text-xs">Última sync: {fmtDate(syncConfig?.ultima_sincronizacion)}</span>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-max">
            <thead>
              <tr style={{ background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                {['Recibido', 'Remitente', 'Asunto', 'Archivo', 'Proveedor', 'Nº Factura', 'Total', 'Estado', 'Confianza', showMotivoCol && 'Motivo', 'Acciones']?.filter(Boolean)?.map(col => (
                  <th key={col} className="px-3 py-3 text-left whitespace-nowrap">
                    <span className="font-oranienbaum text-white/40 text-xs uppercase tracking-wider">{col}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={showMotivoCol ? 11 : 10} className="px-4 py-12 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-8 h-8 rounded-full border-2 border-white/10 border-t-[#22D3EE] animate-spin" />
                    <p className="font-oranienbaum text-white/40 text-sm">Cargando bandeja email...</p>
                  </div>
                </td></tr>
              ) : ingesta?.length === 0 ? (
                <tr><td colSpan={showMotivoCol ? 11 : 10} className="px-4 py-16 text-center">
                  <div className="flex flex-col items-center gap-4">
                    <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: `${CYAN}08`, border: `1px solid ${CYAN}15` }}>
                      <Icon name="Inbox" size={28} style={{ color: `${CYAN}50` }} />
                    </div>
                    <div>
                      <p className="font-oranienbaum text-white/50 text-sm mb-1">No hay emails en la bandeja</p>
                      <p className="font-oranienbaum text-white/25 text-xs">
                        {syncConfig?.watch_active ? 'Las facturas detectadas en Gmail aparecerán aquí automáticamente' : 'Configura Gmail Watch para empezar a recibir facturas automáticamente'}
                      </p>
                    </div>
                  </div>
                </td></tr>
              ) : ingesta?.map((row, i) => {
                const ocrFactura = row?.ocr_metadata?.factura || {};
                const isReprocesando = reprocesandoId === row?.id;
                const canRevisar = row?.estado === 'REQUIERE_REVISION' || row?.estado === 'ERROR';

                return (
                  <tr key={row?.id}
                    style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}
                    className="hover:bg-white/[0.03] transition-colors">
                    <td className="px-3 py-3 whitespace-nowrap">
                      <span className="font-oranienbaum text-white/60 text-xs">{fmtDate(row?.received_at)}</span>
                    </td>
                    <td className="px-3 py-3">
                      <p className="font-oranienbaum text-white/80 text-xs font-semibold">{row?.from_name || '—'}</p>
                      <p className="font-oranienbaum text-white/40 text-[10px]">{row?.from_email || '—'}</p>
                    </td>
                    <td className="px-3 py-3 max-w-[180px]">
                      <p className="font-oranienbaum text-white/65 text-xs truncate">{row?.subject || '—'}</p>
                    </td>
                    <td className="px-3 py-3">
                      <p className="font-oranienbaum text-white/60 text-xs">{row?.filename || '—'}</p>
                      <p className="font-oranienbaum text-white/25 text-[10px]">{row?.mime_type}</p>
                    </td>
                    <td className="px-3 py-3">
                      {row?.proveedor?.nombre ? (
                        <span className="font-oranienbaum text-white/75 text-xs">{row?.proveedor?.nombre}</span>
                      ) : row?.proveedor_detectado ? (
                        <span className="font-oranienbaum text-amber-400/80 text-xs">{row?.proveedor_detectado} ⚠</span>
                      ) : (
                        <span className="font-oranienbaum text-white/25 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <span className="font-oranienbaum text-white/70 text-xs">{row?.numero_factura_ocr || ocrFactura?.numero_factura || '—'}</span>
                    </td>
                    <td className="px-3 py-3">
                      <span className="font-oranienbaum text-white/70 text-xs">
                        {ocrFactura?.total != null ? fmtEur(ocrFactura?.total) : (row?.factura?.total != null ? fmtEur(row?.factura?.total) : '—')}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <EstadoBadge estado={row?.estado} />
                    </td>
                    <td className="px-3 py-3">
                      {row?.ocr_confianza != null ? (
                        <span className="font-oranienbaum text-xs" style={{ color: row?.ocr_confianza >= 80 ? ACCENT : row?.ocr_confianza >= 60 ? AMBER : RED }}>
                          {Math.round(row?.ocr_confianza)}%
                        </span>
                      ) : <span className="font-oranienbaum text-white/25 text-xs">—</span>}
                    </td>
                    {/* MOTIVO column — only for REQUIERE_REVISION tab */}
                    {showMotivoCol && (
                      <td className="px-3 py-3">
                        {row?.motivo_revision ? (
                          <span className="font-oranienbaum text-amber-400 text-xs px-2 py-0.5 rounded-full whitespace-nowrap"
                            style={{ background: `${AMBER}10`, border: `1px solid ${AMBER}20` }}>
                            {MOTIVO_LABELS?.[row?.motivo_revision] || row?.motivo_revision}
                          </span>
                        ) : (
                          <span className="font-oranienbaum text-white/25 text-xs">—</span>
                        )}
                      </td>
                    )}
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1">
                        {/* Revisar button — prominent for REQUIERE_REVISION */}
                        {canRevisar && (
                          <button onClick={() => setRevision(row)}
                            title="Revisar factura"
                            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-oranienbaum font-semibold transition-colors"
                            style={{ background: `${ORANGE}15`, color: ORANGE, border: `1px solid ${ORANGE}30` }}>
                            <Icon name="ClipboardEdit" size={11} />Revisar
                          </button>
                        )}
                        {/* Reprocesar inline for ERROR */}
                        {row?.estado === 'ERROR' && row?.storage_path && !canRevisar && (
                          <button onClick={() => handleReprocesar(row?.id)} disabled={isReprocesando}
                            title="Reprocesar" className="p-1.5 rounded-lg transition-colors hover:bg-white/10 disabled:opacity-50"
                            style={{ color: ORANGE }}>
                            {isReprocesando
                              ? <div className="w-3.5 h-3.5 rounded-full border border-white/20 border-t-orange-400 animate-spin" />
                              : <Icon name="RefreshCw" size={14} />}
                          </button>
                        )}
                        {/* Actions menu */}
                        <RowActionsMenu
                          row={row}
                          onVerDetalle={() => setDetalle(row)}
                          onRevisar={() => setRevision(row)}
                          onVerDocumento={() => handleVerDocumento(row)}
                          onDescargar={() => handleDescargar(row)}
                          onReprocesar={() => handleReprocesar(row?.id)}
                          onNoFactura={() => setConfirm({ type: 'noFactura', row })}
                          onEliminar={() => setConfirm({ type: 'eliminar', row })}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── MODALS ── */}
      {detalle && (
        <DetalleModal
          ingesta={detalle}
          onClose={() => setDetalle(null)}
          onIgnorar={handleIgnorar}
          onReprocesar={handleReprocesar}
        />
      )}

      {revision && (
        <FacturaRevisionModal
          ingesta={revision}
          onClose={() => setRevision(null)}
          onRefresh={load}
        />
      )}

      {showConfig && (
        <ConfigPanel
          syncConfig={syncConfig}
          onClose={() => setShowConfig(false)}
          onSaved={load}
        />
      )}

      {/* ── CONFIRM DIALOGS ── */}
      {confirm?.type === 'eliminar' && (
        <ConfirmDialog
          title="Eliminar factura"
          message="¿Seguro que quieres eliminar esta factura? El documento original se conserva en Storage."
          details={[
            `Proveedor: ${confirm?.row?.proveedor?.nombre || confirm?.row?.proveedor_detectado || '—'}`,
            `Nº Factura: ${confirm?.row?.numero_factura_ocr || confirm?.row?.factura?.numero_factura || '—'}`,
            `Total: ${fmtEur(confirm?.row?.factura?.total || confirm?.row?.ocr_metadata?.factura?.total)}`,
          ]}
          confirmLabel="Eliminar"
          confirmColor={RED}
          onConfirm={() => { const r = confirm?.row; setConfirm(null); handleEliminar(r); }}
          onCancel={() => setConfirm(null)}
        />
      )}
      {confirm?.type === 'noFactura' && (
        <ConfirmDialog
          title="Marcar como no factura"
          message="Este documento se marcará como IGNORADO. La factura ERP asociada será eliminada si existe. El documento original se conserva."
          details={[]}
          confirmLabel="Confirmar"
          confirmColor={PURPLE}
          onConfirm={() => { const r = confirm?.row; setConfirm(null); handleNoFactura(r); }}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
};

export default BandejaEmail;
